import { EllipsisVerticalIcon } from '@heroicons/react/24/solid';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import * as Yup from 'yup';
import { archiveApplicationForm, getApplicationFormDetails, patchApplicationForm } from '@/api/Rover/ApplicationForm';
import type { IPatchApplicationFormServicePayload } from '@/api/Rover/ApplicationForm/types';
import UnAuthorizedMessage from '@/components/Permission/UnauthorizedMessage';
import Badge from '@/components/UI/Badge';
import Button from '@/components/UI/Button';
import ReactSelect from '@/components/UI/Form/ReactSelect';
import Modal from '@/components/UI/Modal';
import SimpleDropDown, { DropDownItem } from '@/components/UI/SimpleDropDown';
import { RoverReactQueryKey } from '@/constants/ReactQueryKeys';
import { useOrgTitles } from '@/hooks/ReactQueryHooks/useTitlesQuery';
import { useUsers } from '@/hooks/ReactQueryHooks/useUsersQuery';
import { useWorkspaces } from '@/hooks/ReactQueryHooks/useWorkspaceQuery';
import useEnvironment from '@/hooks/useEnvironment';
import { usePermission } from '@/hooks/usePermission';
import { notifyUser } from '@/store/Toast';
import type { ReactSelectOption } from '@/types/Constants';
import { EPermission } from '@/types/Permission';
import type { User } from '@/types/User';
import { sanitizeResponseError } from '@/utils';
import { ApplicationNav } from '../ApplicationNav';
import { applicationFormTypes } from '../NewApplicationForm';
import EditableItem from './EditableItem';

export const applicationFormTypeMapping: { [key: string]: string } = {
  CLIENT_APPLICATION: 'Client Application',
  PARTNER_PROPOSAL: 'Partner Proposal',
};

const ApplicationFormDetails = () => {
  const { applicationFormId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasPermission } = usePermission();
  const env = useEnvironment();
  const host = window.location.host;
  const protocol = env.isDev ? 'http' : 'https';

  const fetchUsers = useUsers({ params: { active: 'true' } });

  const users = fetchUsers.data?.data;

  const { data: fetchOrgTitles } = useOrgTitles({
    params: { perPage: 200, page: 1 },
  });

  const authorizedProjectLeadRoles = ['SUPER ADMIN', 'ADMIN', 'PROJECT MANAGER'];

  const authorizedProjectLeadUsers: User[] = [];

  users?.forEach((user: User) => {
    if (authorizedProjectLeadRoles.includes(user.role.toUpperCase())) {
      authorizedProjectLeadUsers.push(user);
    }
  });

  const validLeadOptions = authorizedProjectLeadUsers.map(({ id, name, email }) => ({
    label: `${name}`,
    value: id.toString(),
  }));

  const [changeProgramForm, setChangeProgramForm] = useState<{
    program: ReactSelectOption;
    showModal: boolean;
  }>({
    program: {
      label: '',
      value: '',
    },
    showModal: false,
  });
  const fetchWorkspaces = useWorkspaces({});
  const workspaces: any = fetchWorkspaces?.data?.filter((w) => w?.name !== 'All Program');

  //Tanstack: version 5 query implementation
  const fetchApplicationFormDetails = useQuery({
    queryKey: [RoverReactQueryKey.GET_APPLICATION_FORM_DETAILS, applicationFormId],
    queryFn: () =>
      getApplicationFormDetails({
        applicationFormId: applicationFormId || '',
      }),
  });

  const applicationFormDetails = fetchApplicationFormDetails?.data?.data;

  const currentFormWorkspace = workspaces?.find((w: any) => w?.id === applicationFormDetails?.workspaceId);

  const updateApplicationForm = useMutation(
    (data: Partial<IPatchApplicationFormServicePayload>) =>
      patchApplicationForm({
        data,
        applicationFormId: applicationFormId || '',
      }),
    {
      onSuccess: (_, payload) => {
        //Tanstack: version 5 query implementation
        queryClient.invalidateQueries({ queryKey: [RoverReactQueryKey.GET_APPLICATION_FORM_DETAILS] });
        if (payload?.workspaceId) {
          queryClient.invalidateQueries({ queryKey: [RoverReactQueryKey.GET_APPLICATION_FORMS] });
          navigate('/app/rover/application_forms');
        }
      },
      onError: (error: any) => {
        const resError = sanitizeResponseError(error);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: resError,
            toastType: 'failed',
          })
        );
      },
    }
  );

  const updateArchiveApplicationForm = useMutation(() => archiveApplicationForm(applicationFormId || ''), {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [RoverReactQueryKey.GET_APPLICATION_FORMS] });
      navigate('/app/rover/application_forms');
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: `${applicationFormDetails?.name} archived successfully.`,
          toastType: 'success',
        })
      );
    },
    onError: (error: any) => {
      const resError = sanitizeResponseError(error);
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: resError,
          toastType: 'failed',
        })
      );
    },
  });

  if (!hasPermission([EPermission.READ_APPLICATION_FORM])) {
    return <UnAuthorizedMessage title='Unauthorized' message='You do not have permission to view this application form settings.' />;
  }
  return (
    <>
      <ApplicationNav />
      <div className='overflow-hidden bg-white shadow sm:rounded-lg'>
        <div className='px-4 py-5 sm:px-6 flex justify-between items-start'>
          <div>
            <h3 className='text-lg font-medium leading-6 text-gray-900'>Form Settings</h3>
            <p className='mt-1 max-w-2xl text-sm text-gray-500'>Details of an application form settings.</p>
          </div>
          {hasPermission([EPermission.UPDATE_APPLICATION_FORM, EPermission.ARCHIVE_APPLICATION_FORM]) && (
            <div className='text-neutralColor-normal cursor-pointer -mr-2 hover:bg-neutralColor-light rounded-full p-1'>
              <SimpleDropDown toggle={<EllipsisVerticalIcon className='w-5 h-5' />}>
                <DropDownItem
                  id='change-program'
                  onClick={() =>
                    setChangeProgramForm((prevState) => ({
                      ...prevState,
                      showModal: true,
                    }))
                  }
                >
                  Change Program
                </DropDownItem>
                <DropDownItem
                  id={applicationFormDetails?.active ? 'deactivate' : 'activate'}
                  onClick={() =>
                    updateApplicationForm.mutate({
                      active: !applicationFormDetails?.active,
                    })
                  }
                >
                  {applicationFormDetails?.active ? 'Deactivate' : 'Activate'}
                </DropDownItem>
                {hasPermission([EPermission.ARCHIVE_APPLICATION_FORM]) && (
                  <DropDownItem id='archive' onClick={() => updateArchiveApplicationForm.mutate()}>
                    Archive
                  </DropDownItem>
                )}
              </SimpleDropDown>
            </div>
          )}
        </div>
        <div className='border-t border-gray-200 px-4 py-5 sm:px-6'>
          <dl className='grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2'>
            <div className='sm:col-span-1'>
              <EditableItem
                name='logo'
                label='Logo'
                inputType='file'
                value={applicationFormDetails?.logo}
                defaultValue={applicationFormDetails?.logo}
              />
              {applicationFormDetails?.logo && (
                <span className='font-medium leading-none text-green-500 font-bold text-xl'>
                  <img src={applicationFormDetails?.logo} alt='application-form-logo' className='object-contain w-1/3' />
                </span>
              )}
            </div>
            <div className='sm:col-span-1'>
              <dt className='text-sm font-medium text-gray-500'>Status</dt>
              <dd className='mt-1 text-sm text-gray-900'>
                <Badge
                  text={applicationFormDetails?.active ? 'Active' : 'In Active'}
                  status={applicationFormDetails?.active ? 'ACTIVE' : 'INACTIVE'}
                />
              </dd>
            </div>
            <div className='sm:col-span-1'>
              <EditableItem
                name='name'
                label='Name'
                inputType='text'
                value={applicationFormDetails?.name}
                defaultValue={applicationFormDetails?.name}
                validationSchema={Yup.object({
                  name: Yup.string()
                    .required('Required')
                    .min(2, 'This should be at least 2 character')
                    .max(150, 'This should not be more than 150 characters.'),
                })}
              />
            </div>
            <div className='sm:col-span-1'>
              <EditableItem
                name='description'
                label='Description'
                inputType='textarea'
                value={applicationFormDetails?.description}
                defaultValue={applicationFormDetails?.description}
              />
            </div>
            <div className='sm:col-span-1'>
              <EditableItem
                name='formType'
                label='Form Type'
                inputType='select'
                options={applicationFormTypes}
                value={applicationFormTypeMapping[applicationFormDetails?.formType]}
                defaultValue={applicationFormTypes.find((item) => item.value === applicationFormDetails?.formType)}
              />
            </div>
            <div className='sm:col-span-1'>
              <EditableItem
                name='titles'
                label='Viewers'
                inputType='multiselect'
                options={fetchOrgTitles?.data?.map((d) => {
                  return {
                    label: d.name,
                    value: d.id.toString(),
                  };
                })}
                value={applicationFormDetails?.titles
                  ?.map((d: number) => {
                    const filterData = fetchOrgTitles?.data?.find((org) => org.id === d);
                    return filterData?.name;
                  })
                  .join(', ')}
                defaultValue={applicationFormDetails?.titles?.map((d: number) => {
                  const filterData = fetchOrgTitles?.data?.find((org) => org?.id === d);
                  return {
                    label: filterData?.name,
                    value: filterData?.id,
                  };
                })}
              />
            </div>
            <div className='sm:col-span-1'>
              <EditableItem
                name='leadById'
                label='Lead By'
                inputType='select'
                options={validLeadOptions}
                value={validLeadOptions.find((lead) => lead.value === applicationFormDetails?.leadById)?.label}
                defaultValue={validLeadOptions.find((lead) => lead.value === applicationFormDetails?.leadById)?.label}
              />
            </div>
            <div className='sm:col-span-1'>
              <EditableItem
                name='confirmationMessage'
                label='Confirmation Message'
                inputType='textarea'
                value={applicationFormDetails?.confirmationMessage}
                defaultValue={applicationFormDetails?.confirmationMessage}
              />
            </div>
            <div className='sm:col-span-1'>
              <EditableItem
                name='emailSubject'
                label='Email Subject'
                inputType='text'
                value={applicationFormDetails?.emailSubject}
                defaultValue={applicationFormDetails?.emailSubject}
              />
            </div>
            <div className='sm:col-span-1'>
              <EditableItem
                name='emailMessage'
                label='Email Message'
                inputType='textarea'
                value={applicationFormDetails?.emailMessage}
                defaultValue={applicationFormDetails?.emailMessage}
              />
            </div>
            <div className='sm:col-span-1'>
              <EditableItem
                name='denialMessage'
                label='Submission Deny Message'
                inputType='textarea'
                value={applicationFormDetails?.denialMessage}
                defaultValue={applicationFormDetails?.denialMessage}
              />
            </div>
            <div className='sm:col-span-1'>
              <EditableItem
                name='formInactiveMessage'
                label='Form Inactive Message'
                inputType='textarea'
                value={applicationFormDetails?.formInactiveMessage}
                defaultValue={applicationFormDetails?.formInactiveMessage}
              />
            </div>
            <div className='sm:col-span-1'>
              <EditableItem
                name='approvalMessage'
                label='Submission Approval Message'
                inputType='textarea'
                value={applicationFormDetails?.approvalMessage}
                defaultValue={applicationFormDetails?.approvalMessage}
              />
            </div>
            <div className='sm:col-span-1'>
              <EditableItem
                name='templateCode'
                label='Form Code'
                inputType='text'
                value={applicationFormDetails?.templateCode}
                defaultValue={applicationFormDetails?.templateCode}
                validationSchema={Yup.object({
                  templateCode: Yup.string()
                    .required('Required')
                    .min(8, 'This should be at least 8 character')
                    .max(15, 'This should not be more than 15 characters.'),
                })}
              />
            </div>
            <div className='sm:col-span-1'>
              <dt className='text-sm font-medium text-gray-500'>Form Link</dt>
              <dd className='mt-1 text-sm text-gray-900'>
                <a
                  href={`${protocol}://${host}/applications/${applicationFormDetails?.id}`}
                  target='_blank'
                  rel='noreferrer'
                  className='underline cursor-pointer'
                >
                  {protocol}://{host}/applications/
                  {applicationFormDetails?.id}
                </a>
              </dd>
            </div>
            <div className='sm:col-span-1'>
              <dt className='text-sm font-medium text-gray-500'>Created By</dt>
              <dd className='mt-1 text-sm text-gray-900'>{applicationFormDetails?.createdBy?.name}</dd>
            </div>
            <div className='sm:col-span-1'>
              <dt className='text-sm font-medium text-gray-500'>Created At</dt>
              <dd className='mt-1 text-sm text-gray-900'>{applicationFormDetails?.createdAt}</dd>
            </div>
          </dl>
        </div>
      </div>
      {changeProgramForm.showModal && (
        <Modal>
          <div>
            <h2 className='mt-0'>Project Workspace</h2>
            <ReactSelect
              id='projectWorkspace'
              name='projectWorkspace'
              options={
                workspaces?.map((w: IGetWorkspacesServiceResData) => ({
                  label: w.name,
                  value: w.id,
                })) ?? []
              }
              defaultValue={{
                label: (currentFormWorkspace as IGetWorkspacesServiceResData)?.name,
                value: (currentFormWorkspace as IGetWorkspacesServiceResData)?.id,
              }}
              onChange={(option) => {
                setChangeProgramForm((prevState) => ({
                  ...prevState,
                  program: option as unknown as ReactSelectOption,
                }));
              }}
            />
            <div className='flex gap-4 justify-end mt-5'>
              <Button
                label='Cancel'
                btnStyle='btn-neutral'
                onClick={() =>
                  setChangeProgramForm((prevState) => ({
                    ...prevState,
                    showModal: false,
                  }))
                }
              />
              <Button
                label='Change'
                btnStyle='btn-primary'
                onClick={() => {
                  if (changeProgramForm.program?.value.toString().trim() !== '') {
                    updateApplicationForm.mutate({
                      workspaceId: Number(changeProgramForm.program?.value),
                    });
                  }

                  setChangeProgramForm((prevState) => ({
                    ...prevState,
                    showModal: false,
                  }));
                }}
              />
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default ApplicationFormDetails;
